// The code dialog (prototype code): an organization's own Python function on
// an additional attribute — its source, Save, and "Compile and check".
//
// The source lives on the server (`twasta_uda_code`, plan D12): it is read
// when the dialog opens (`getCode`), saved on Save and on Close (`saveCode` —
// a changed source is unchecked again), and checked by the server
// (`checkCode`, which stores the source with its results). Publishing refuses
// a function that is unchecked or failed its check. The check runs the
// function in the real business-logic sandbox: read-only data, additional
// attributes only. A host that cannot run Python functions (a J2EE server with
// no Python sidecar) answers 501/503, shown as a notice.
//
// Design mode (the transaction designer's standard attributes): a standard
// attribute's rules call the transaction's OWN business logic, written in the
// designer's Business logic files and run with the application's rights — not
// an organization's function — so nothing is stored here.

import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react'
import { AlertTriangle, Check, CircleCheck, Info, Save } from 'lucide-react'
import { tw } from '../../testHooks'
import { KitModal } from '../../designerKit/KitModal'
import { CODE_SOURCE_MAX } from '../limits'
import type { UdaCodeResult, UdaCodeStatus } from '../types'
import { useSetup } from './state'
import { Banner } from './ui'

function starter(fnName: string): string {
  return `def ${fnName}(header, form, attributes, drivers, db):\n    # Read-only database. Return {"updates": {...}, "protect": {...},\n    # "warnings": [...]} naming additional attributes only.\n    return {}\n`
}

function errorInfo(err: unknown): { status?: number; message: string } {
  const e = err as { status?: number; message?: string; detail?: { message?: string }; response?: { status?: number; data?: { detail?: { message?: string } | string } } }
  const status = e?.status ?? e?.response?.status
  const d = e?.response?.data?.detail
  const message = e?.detail?.message || (typeof d === 'string' ? d : d?.message) || e?.message || 'The request failed'
  return { status, message }
}

const STATUS_TEXT: Record<UdaCodeStatus, string> = {
  checked: 'Checked',
  failed: 'Check failed',
  unchecked: 'Not checked',
  needs_recheck: 'Needs re-check',
}

/** Why publishing will refuse the function, in the dialog's words. */
const STATUS_NOTE: Partial<Record<UdaCodeStatus, string>> = {
  failed: 'The settings can be saved with this function, but they cannot be published until it passes its check.',
  unchecked: 'Check the function before publishing — publishing refuses a function that has not been checked.',
  needs_recheck: 'The additional attributes this function reads have changed since it was checked. Check it again before publishing.',
}

function StatusPill({ status }: { status: UdaCodeStatus | null }) {
  if (!status) return null
  if (status === 'checked') return <span className="uds-pill uds-p-live">{STATUS_TEXT[status]}</span>
  if (status === 'failed') {
    return <span className="uds-pill" style={{ background: 'var(--color-error-weak)', color: 'var(--color-error)' }}>{STATUS_TEXT[status]}</span>
  }
  return <span className="uds-pill uds-p-draft">{STATUS_TEXT[status]}</span>
}

export default function CodeDialog({ fnName, title, onClose, codeEditor: Editor }: {
  fnName: string
  title?: string
  onClose: () => void
  codeEditor?: ComponentType<{ value: string; onChange: (v: string) => void; language: string }>
}) {
  const { transport, objName, mode } = useSetup()
  const design = mode === 'design'
  const [source, setSource] = useState<string>(() => starter(fnName))
  const [loading, setLoading] = useState(!design)
  const [busy, setBusy] = useState<'check' | 'save' | null>(null)
  const [dirty, setDirty] = useState(false)
  const [status, setStatus] = useState<UdaCodeStatus | null>(null)
  const [published, setPublished] = useState<{ published: boolean; same: boolean } | null>(null)
  const [results, setResults] = useState<UdaCodeResult[] | null>(null)
  const [unavailable, setUnavailable] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  // Read the stored function once (tenant mode).
  useEffect(() => {
    if (design) return
    let cancelled = false
    setLoading(true)
    transport.getCode(objName, fnName).then(info => {
      if (cancelled) return
      if (info.exists && typeof info.source === 'string') setSource(info.source)
      setStatus(info.exists ? (info.status || 'unchecked') : null)
      setResults(info.results?.length ? info.results : null)
      setPublished({ published: !!info.published, same: !!info.published_same })
    }).catch(err => {
      if (cancelled) return
      const { status: st, message } = errorInfo(err)
      if (st === 501 || st === 503) setUnavailable(message)
      else setError(`The function could not be read: ${message}`)
      console.error('[uda setup] get-code failed', st, message)
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [design, transport, objName, fnName])

  const tooLong = source.length > CODE_SOURCE_MAX

  const change = (v: string) => {
    setSource(v)
    setDirty(true)
    setError(null)
  }

  const save = useCallback(async (): Promise<boolean> => {
    if (design || !dirty) return true
    if (tooLong) {
      setError(`The source is ${source.length} characters; at most ${CODE_SOURCE_MAX} are allowed.`)
      return false
    }
    setBusy('save')
    setError(null)
    try {
      const r = await transport.saveCode(objName, fnName, source)
      if (!alive.current) return true
      setStatus(r.status)
      if (r.status !== 'checked') setResults(null)
      setDirty(false)
      return true
    } catch (err) {
      const { status: st, message } = errorInfo(err)
      console.error('[uda setup] save-code failed', st, message)
      if (alive.current) {
        if (st === 501 || st === 503) setUnavailable(message)
        else setError(`The function was not saved: ${message}`)
      }
      return false
    } finally {
      if (alive.current) setBusy(null)
    }
  }, [design, dirty, tooLong, source, transport, objName, fnName])

  const check = async () => {
    if (tooLong) {
      setError(`The source is ${source.length} characters; at most ${CODE_SOURCE_MAX} are allowed.`)
      return
    }
    setBusy('check')
    setResults(null)
    setUnavailable(null)
    setError(null)
    try {
      const r = await transport.checkCode(objName, fnName, source)
      if (!alive.current) return
      setResults(r.results || [])
      setStatus(r.status || null)
      setDirty(false)  // the check stores the source it checked
    } catch (err) {
      const { status: st, message } = errorInfo(err)
      console.info('[uda setup] check-code answered', st, message)
      if (!alive.current) return
      if (st === 501 || st === 503) setUnavailable(message)
      else setResults([['e', message]])
    } finally {
      if (alive.current) setBusy(null)
    }
  }

  const close = async () => {
    if (await save()) onClose()
  }

  if (design) {
    return (
      <KitModal open onClose={onClose} title={fnName} size="wide"
        footer={<button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>}>
        <div className="uds-dlg" style={{ display: 'grid', gap: 10 }}>
          <Banner tone="i">
            A standard attribute&apos;s rule calls the transaction&apos;s own business logic. Write
            <b className="uds-mono"> {fnName} </b> with the transaction&apos;s other business logic
            functions; it runs with the application&apos;s rights. Organizations write their own
            functions from Customize → Additional attributes, where they run read-only and change
            additional attributes only.
          </Banner>
        </div>
      </KitModal>
    )
  }

  return (
    <KitModal open onClose={() => { void close() }} title={fnName} size="wide"
      footer={(
        <>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy} onClick={() => { void close() }}>Close</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!!busy || loading || !dirty || tooLong}
            onClick={() => { void save() }} {...tw('action', 'uda-code-save')}>
            {busy === 'save' ? <span className="uds-spin" /> : <Save size={14} />} Save
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={!!busy || loading || tooLong}
            onClick={() => { void check() }} {...tw('action', 'uda-code-check')}>
            {busy === 'check' ? <span className="uds-spin" /> : <Check size={14} />} Compile and check
          </button>
        </>
      )}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="uds-hint" style={{ margin: 0, flex: 1, minWidth: 220 }}>
            Python, in the business-logic sandbox{title ? `, for ${title}` : ''}. Read-only database. May change additional attributes only.
          </div>
          {dirty ? <span className="uds-pill uds-p-draft">Unsaved</span> : <StatusPill status={status} />}
          {published?.published && !dirty && (
            <span className="uds-pill uds-p-acc">{published.same ? 'Published' : 'Changed since published'}</span>
          )}
        </div>
        {!loading && !dirty && status && STATUS_NOTE[status] && (
          <Banner tone={status === 'failed' ? 'e' : 'w'}>{STATUS_NOTE[status]}</Banner>
        )}
        {loading ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--color-text-secondary)' }}>
            <span className="uds-spin" /> Reading the function…
          </div>
        ) : Editor
          ? <Editor value={source} onChange={change} language="python" />
          : (
            <textarea className="uds-inp uds-code" style={{ minHeight: 260, whiteSpace: 'pre', fontFamily: 'inherit' }} spellCheck={false}
              value={source} onChange={e => change(e.target.value)} aria-label="Function source" />
          )}
        {tooLong && (
          <Banner tone="e">The source is {source.length} characters; at most {CODE_SOURCE_MAX} are allowed.</Banner>
        )}
        {error && <Banner tone="e">{error}</Banner>}
        {unavailable && <Banner tone="i">{unavailable}</Banner>}
        {results && !results.length && <Banner tone="i">Nothing to report.</Banner>}
        {(results || []).map(([kind, msg], i) => (
          <div key={i} className="uds-li" style={{ marginTop: 0 }}>
            {kind === 'ok' ? <CircleCheck size={15} style={{ color: 'var(--color-success)' }} />
              : kind === 'w' ? <AlertTriangle size={15} style={{ color: 'var(--color-warning)' }} />
                : kind === 'e' ? <AlertTriangle size={15} style={{ color: 'var(--color-error)' }} />
                  : <Info size={15} />}
            <span className="t" style={{ whiteSpace: 'normal' }}>{msg}</span>
          </div>
        ))}
      </div>
    </KitModal>
  )
}
